/**
 * React hook for PostHog feature flags
 */

import { useState, useEffect } from 'react';
import { usePosthog } from './usePosthog';
import { featureFlagManager } from '~/services/posthog';

/**
 * Hook to check if a feature flag is enabled
 * Uses global feature flag manager to prevent memory leaks
 */
export const useFeatureFlag = (flagKey: string, defaultValue: boolean = false): boolean => {
  const posthogService = usePosthog();
  const [isEnabled, setIsEnabled] = useState<boolean>(defaultValue);

  useEffect(() => {
    // Initialize the manager with PostHog service
    featureFlagManager.initialize(posthogService);

    // Get initial flag value
    const initialValue = featureFlagManager.getFeatureFlag(flagKey, defaultValue);
    setIsEnabled(initialValue);

    // Subscribe to flag updates
    const unsubscribe = featureFlagManager.subscribe(flagKey, (enabled) => {
      setIsEnabled(enabled);
    });

    // Reload flags to ensure we have the latest
    featureFlagManager.reloadFeatureFlags();

    // Cleanup subscription on unmount or dependency change
    return () => {
      unsubscribe();
    };
  }, [flagKey, defaultValue, posthogService]);

  return isEnabled;
};